import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FlagOutlined as KraIcon,
  CheckCircleOutline as KpiIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/api';

const cycleColors = {
  Q1: 'primary',
  Q2: 'secondary',
  Q3: 'warning',
  Q4: 'error',
  Annual: 'success',
};

const KpikriTab = () => {
  const { getAuthHeaders } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchForms = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/performance/my-forms`,
          { headers: getAuthHeaders() }
        );
        if (response.data?.success) {
          setForms(response.data.forms);
        }
      } catch (err) {
        setError('Failed to load KRA/KPI data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchForms();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!forms.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No KRA/KPI forms have been assigned to you yet. Check back once your performance form is published.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
        My KRAs &amp; KPIs
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Reference guide for your assigned Key Result Areas and Key Performance Indicators.
      </Typography>

      <Stack spacing={3}>
        {forms.map(({ reviewId, form, status }) => {
          if (!form) return null;
          return (
            <Card key={reviewId} variant="outlined">
              <CardContent>
                {/* Form header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {form.title}
                  </Typography>
                  <Chip
                    label={`${form.reviewCycle} ${form.reviewYear}`}
                    color={cycleColors[form.reviewCycle] || 'default'}
                    size="small"
                  />
                  <Chip
                    label={status}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>

                {form.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {form.description}
                  </Typography>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* KRAs */}
                {form.kras && form.kras.length > 0 ? (
                  form.kras.map((kra, kraIdx) => (
                    <Accordion key={kraIdx} disableGutters elevation={0}
                      sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: 1,
                        '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                          <KraIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                            {kra.name}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {kra.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            {kra.description}
                          </Typography>
                        )}

                        {kra.kpis && kra.kpis.length > 0 ? (
                          <Stack spacing={1}>
                            {kra.kpis.map((kpi, kpiIdx) => (
                              <Box key={kpiIdx}
                                sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start',
                                  p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <KpiIcon sx={{ color: 'success.main', mt: 0.2 }} fontSize="small" />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {kpi.name}
                                  </Typography>
                                  {kpi.description && (
                                    <Typography variant="body2" color="text.secondary">
                                      {kpi.description}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No KPIs defined under this KRA.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No KRAs defined in this form.
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

export default KpikriTab;
